import { ExclamationTriangleIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { setLoanData, setLoanGroupData } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { OtherPaymentsEditor, sumOtherPayments } from 'components/OtherPayments'
import { Payee } from 'components/PayeeCombo'
import { RateIndexValuesModal } from 'components/RateIndexes'
import { InputType, SettingKey } from 'config'
import { useSharedLoan } from 'context/SharedLoanContext'
import { usePermissions } from 'hooks/usePermissions'
import {
  applySectionOrder,
  buildCustomSections,
  customFieldToInput,
  getBuiltInSectionTab,
  IServicingInfoConfig,
  parseSections,
  SERVICING_INFO_TABS,
} from 'pages/Admin/AdminTools/Configuration/ServicingInformation/types'
import { RolodexModal } from 'pages/Loan/Parties/Modal/RolodexModal'
import { ReminderDetailModal } from 'pages/Reminders/ReminderDetailModal'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  type InsuranceAiExtractData,
  addNewLoanCharge,
  deleteLoanCharge,
  getAdminConfig,
  getAdminEmails,
  getGenerateDocuments,
  getInternalUserEmails,
  getLoanPayees,
  getServicingNotes,
  postServicingNotes,
  updateLoanFields,
} from 'services'
import { deleteReminder } from 'services/apis/reminder'
import { Button, ButtonGroup, Toggle, Tooltip } from 'stories/components'
import { OverrideCaclModal } from 'stories/components/OverrideCalcModal/OverrideCalcModal'
import { confirm, formatDateYMD, getFullName, InputValidate, openAuditLog, thousandSeperator } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { LoanChargeType } from '../LoanCharges'
import { AdjustBalanceModal } from './AdjustBalanceModal'
import { AmortizationScheduleModal } from './AmortizationScheduleModal'
import { ArmAdjustmentPreview } from './ArmAdjustmentPreview'
import { calcKeys, fieldEditPermissions, floodCertKeys } from './constants'
import { InsuranceAiExtractModal } from './InsuranceAiExtractModal'
import { PaymentInterestCalcModal } from './PaymentInterestCalcModal'
import { PaymentOptionsModal } from './PaymentOptionsModal'

const tabs = SERVICING_INFO_TABS

/**
 * Tabs that own a saved reminder id. Only these render the reminder footer; the reorganized
 * Property / Default tabs have no backing reminder field, and Loan Details no longer carries one
 * (its reminder was about the loan as a whole, which the Reminders screen already covers -
 * Insurance and Taxes reminders are about a dated policy or bill, which is why they stay).
 */
const REMINDER_TAB_KEYS: Record<string, string> = {
  Insurance: 'insuranceReminderId',
  Taxes: 'taxesReminderId',
}

/**
 * Payments-group fields held back until after the Other Payments editor. Total Payment sums the
 * whole breakdown, Other Payments included, so it only reads as a total when it sits below them.
 * `ptSpacer` is the grid spacer that keeps Total in the right-hand column, so it travels with it.
 */
const TRAILING_PAYMENT_KEYS = ['ptSpacer', 'totalPayment']

/**
 * The adjustable-rate block. A fixed-rate loan has nothing to say here, so the whole group is
 * hidden rather than shown empty - the same treatment Accrual Period gets on a Non-Dutch loan.
 */
const ARM_FIELD_KEYS = [
  'armGroup',
  'armIndex',
  'armIndexValue',
  'armLookbackDays',
  'armMargin',
  'armFixedForMonths',
  'armAdjustEveryMonths',
  'armNextAdjustmentDate',
  'armInitialCap',
  'armPeriodicCap',
  'armLifeCap',
  'armFloor',
  'armCeiling',
  'armRounding',
]

/**
 * The Servicing Fee Types each fee input applies to. A field not listed here is always shown.
 * '% of PITI' is what '% of Total Payment' was called when it shipped: a loan configured before
 * the rename can still carry it, so the percent inputs stay on screen for one.
 */
const PERCENT_FEE_TYPES = ['% of P&I Payment', '% of Total Payment', '% of PITI', '% of UPB (Annual)']
const SERVICING_FEE_FIELD_TYPES: Record<string, string[]> = {
  servicingFeeAmount: ['Fixed Amount'],
  servicingFeePercent: PERCENT_FEE_TYPES,
  // A per-payment cap only means something when the fee is computed rather than typed.
  servicingFeeMax: PERCENT_FEE_TYPES,
}

/**
 * Total Payment is the live sum of these breakdown component fields: P&I + escrow + the borrower
 * servicing fee billed on every regular payment, plus the Other Payments collected with every
 * payment (they are passed through to their payees, but the borrower still owes them, so they
 * belong in the amount due).
 */
const TOTAL_PAYMENT_COMPONENTS = ['principalInterest', 'escrowMonthly', 'servicingFeeDue']

const calcTotalPayment = (data: Record<string, any>) =>
  Number(
    (
      TOTAL_PAYMENT_COMPONENTS.reduce((sum, k) => sum + (Number(String(data[k] ?? '').replace(/,/g, '')) || 0), 0) +
      sumOtherPayments(data.otherPayments || [])
    ).toFixed(2),
  )

/**
 * Editing any of these re-triggers the server-computed P&I (and its "How?"
 * breakdown), so we re-fetch after saving one of them. `interestOnly` drives
 * whether the Dutch P&I accrues interest-only on the full committed amount or
 * amortizes, so a toggle must recompute it. The servicing-fee settings drive the
 * server-computed per-payment fee, which Total Payment includes, and so do the
 * other components of that payment, since the fee can be a percent of them.
 */
/** Interest Calculations toggles that add an unpaid balance to the interest base; kept in one column. */
const INTEREST_BALANCE_TOGGLE_KEYS = ['compoundInterest', 'interestOnUnpaidCharges', 'interestOnUnpaidLateCharges']

/**
 * Fields pinned to a column of the 12-column grid instead of taking the next free slot.
 * - Total Payment is the sum of everything above it, so it sits in the right-hand column under
 *   Other Payments rather than starting a fresh row on the left.
 * - The interest-on-balance toggles stack in the right-hand column of Interest Calculations. Pinned
 *   rather than left to the flow, because the fields ahead of them come and go (Accrual Period on
 *   Dutch loans, fields a tenant hides).
 */
const gridColumnStart = (key: string) => {
  if (key === 'totalPayment') return 'col-start-9'
  if (INTEREST_BALANCE_TOGGLE_KEYS.includes(key)) return 'col-start-5'
  return ''
}

const PI_AFFECTING_KEYS = [
  'currentInterestRate',
  'dailyRateBasis',
  'interestType',
  // Financing the interest reserve in (or holding it out) moves the initial advance, so the
  // Current UPB and the Non-Dutch P&I computed from it have to be re-read after the save.
  'chargeInterestOnReserve',
  'interestOnly',
  'compoundInterest',
  'interestOnUnpaidCharges',
  'interestOnUnpaidLateCharges',
  'nextPaymentDate',
  'servicingFeeType',
  'servicingFeeAmount',
  'servicingFeePercent',
  'servicingFeeMax',
  'paymentFrequency',
  // A '% of Total Payment' fee is a percent of the impound and the pass-through Other Payments
  // too, so editing either has to recompute it rather than leaving a stale fee in Total Payment.
  'escrowMonthly',
  'otherPayments',
]

/**
 * A Dutch loan's P&I is a stored amount the servicer can set by hand, so unlike the
 * server-computed Non-Dutch P&I it does not follow the rate. Editing one of these leaves the
 * stored payment stale with nothing on screen to say so, so we reopen Payment Options with the
 * recalculated figures rather than silently overwriting a hand-entered payment.
 */
const PAYMENT_RECALC_KEYS = ['currentInterestRate', 'interestOnly', 'dailyRateBasis']

/** Balance fields adjustable via a signed ledger charge, mapped to the charge type. */
const ADJUSTABLE_BALANCES: Record<string, LoanChargeType> = {
  unpaidInterestBalance: LoanChargeType.UnpaidInterest,
  unpaidDefaultInterestBalance: LoanChargeType.UnpaidDefaultInterest,
  unpaidChargesBalance: LoanChargeType.LoanCharges,
  unpaidLateChargesBalance: LoanChargeType.UnpaidLateCharges,
}

export default function ServicingNote() {
  const shared = useSharedLoan()
  const isShared = !!shared?.readOnly
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<any>({})
  const [orgData, setOrgData] = useState<any>({})
  const [generateDocuments, setGenerateDocuments] = useState<Record<string, any>>({})
  const [ovData, setOVData] = useState<any>({})
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [insuranceInputs, setInsuranceInputs] = useState<Record<string, InputType>>({})
  const [taxInputs, setTaxInputs] = useState<Record<string, InputType>>({})
  const [extensionInputs, setExtensionInputs] = useState<Record<string, InputType>>({})
  const [tab, setTab] = useState(tabs[0])
  const [isReminderOpen, setIsReminderOpen] = useState(false)
  const [userEmails, setUserEmails] = useState<string[]>([])
  const [reminderIds, setReminderIds] = useState<Record<string, number>>({
    Insurance: -1,
    Taxes: -1,
  })
  const [isShowRolodexModal, setShowRolodexModal] = useState(false)
  const [showAmortization, setShowAmortization] = useState(false)
  const [showPaymentOptions, setShowPaymentOptions] = useState(false)
  const [showPICalc, setShowPICalc] = useState(false)
  // The index whose value history is open from the Index field's Values link.
  const [indexValuesFor, setIndexValuesFor] = useState<{ key: string; name: string; autoUpdated?: boolean } | null>(
    null,
  )
  const [adjustBalance, setAdjustBalance] = useState<{ key: string; title: string } | null>(null)
  const [selectedInsuranceItem, setSelectedInsuranceItem] = useState<number | null>(null)
  const [showInsuranceAiExtract, setShowInsuranceAiExtract] = useState(false)
  const [insuranceAiTargetIndex, setInsuranceAiTargetIndex] = useState<number | null>(null)
  const [insuranceAiEnabled, setInsuranceAiEnabled] = useState(false)
  const [config, setConfig] = useState<IServicingInfoConfig>({
    fields: {},
    sections: {},
    sectionOrder: [],
    customSections: [],
  })
  // Payees for the Other Payments default setup (borrower / participants / vendors / contractors).
  const [payees, setPayees] = useState<Payee[]>([])

  const dispatch = useDispatch()

  const { loan } = useSelector((state: any) => {
    return {
      loan: state.loan,
    }
  })

  const loanNumber = isShared ? shared.loanNumber : setLoanNumber()

  const { hasPermission } = usePermissions()
  const canInsuranceAiExtract =
    !isShared && hasPermission('Loan.Servicing.ServicingInformation.AIExtract') && insuranceAiEnabled

  useEffect(() => {
    if (isShared || !hasPermission('Loan.Servicing.ServicingInformation.AIExtract')) {
      setInsuranceAiEnabled(false)
      return
    }
    getAdminConfig(SettingKey.GLOBAL_SETTINGS).then((settings: any) => {
      setInsuranceAiEnabled(!!settings?.insuranceAiAutofillEnabled)
    })
  }, [isShared, hasPermission])

  /**
   * Field keys the current user may see but not edit. Interest terms, balances,
   * dates and payment amounts are gated per group (see `fieldEditPermissions`);
   * without the permission the input renders read-only and the save is rejected
   * server-side too.
   */
  const readOnlyFieldKeys = useMemo(() => {
    const set = new Set<string>()
    Object.entries(fieldEditPermissions).forEach(([key, permission]) => {
      if (!hasPermission(permission)) set.add(key)
    })
    return set
  }, [hasPermission])

  useEffect(() => {
    Promise.all([getAdminEmails(), getInternalUserEmails()]).then(([adminEmails, internalEmails]) =>
      setUserEmails(Array.from(new Set([...adminEmails, ...internalEmails]))),
    )
    // Shared loan view is read-only and uses peer page data; local payees need a loan-number header
    // that is intentionally not set for /nexys-shared routes.
    if (!isShared && loanNumber) {
      getLoanPayees(loanNumber)
        .then((res: any) => setPayees(Array.isArray(res?.payees) ? res.payees : []))
        .catch(() => setPayees([]))
    }
    getAdminConfig('servicingInfoConfig')
      .then((res: any) => {
        const cfg = (res?.data || {}) as IServicingInfoConfig
        setConfig({
          fields: cfg.fields || {},
          sections: cfg.sections || {},
          sectionOrder: cfg.sectionOrder || [],
          customSections: cfg.customSections || [],
        })
      })
      .catch(() => {})
  }, [isShared, loanNumber])

  useEffect(() => {
    if (isShared) {
      const res = shared.pageData as any
      if (res?.success) {
        setData(res.data)
        setOrgData(res.data)
        setInputs(res.inputs)
        setInsuranceInputs(res.insuranceInputs)
        setTaxInputs(res.taxInputs)
        setExtensionInputs(res.extensionInputs)
        setReminderIds({
          Insurance: res.data.insuranceReminderId || -1,
          Taxes: res.data.taxesReminderId || -1,
        })
      }
      setLoading(false)
      return
    }
    setLoading(true)
    getServicingNotes()
      .then((res: any) => {
        if (res.success) {
          setData(res.data)
          setOrgData(res.data)
          setInputs(res.inputs)
          setInsuranceInputs(res.insuranceInputs)
          setTaxInputs(res.taxInputs)
          setExtensionInputs(res.extensionInputs)

          // Load reminder IDs if they exist
          const newReminderIds = {
            Insurance: res.data.insuranceReminderId || -1,
            Taxes: res.data.taxesReminderId || -1,
          }
          setReminderIds(newReminderIds)
        }
      })
      .finally(() => setLoading(false))
    if (!isShared) {
      getGenerateDocuments(true).then(({ data }) => {
        setGenerateDocuments(data)
      })
    }
  }, [isShared, shared?.pageKey, shared?.pageData])

  /**
   * Apply admin config (renames, hides, reorders) and inject custom sections
   * + their custom fields. Returns a new inputs dict whose key order reflects
   * the configured section order. Hidden fields are left in the dict; the
   * render loop filters them via `hiddenFieldKeys`.
   */
  const mergedInputs = useMemo(() => {
    if (!Object.keys(inputs).length) return inputs

    const builtInSections = parseSections(inputs)
    const customSections = buildCustomSections(config.customSections || [])
    const ordered = applySectionOrder([...builtInSections, ...customSections], config.sectionOrder || [])

    const result: Record<string, InputType> = {}
    ordered.forEach((section) => {
      if (section.isCustom) {
        const customDef = (config.customSections || []).find((s) => `custom:${s.id}` === section.key)
        const sectionTitle = customDef?.title || section.title
        result[section.key] = {
          inputType: 'section',
          title: sectionTitle,
          span: 'full',
        } as InputType
        customDef?.fields.forEach((f) => {
          result[f.key] = customFieldToInput(f, { loanNumber })
        })
      } else {
        const sectionInput = cloneDeep(inputs[section.key])
        if (sectionInput) {
          const overrideTitle = config.sections?.[section.key]?.title
          if (overrideTitle) sectionInput.title = overrideTitle
          result[section.key] = sectionInput
        }
        section.fieldKeys.forEach((fk) => {
          const fInput = cloneDeep(inputs[fk])
          if (!fInput) return
          const ft = config.fields?.[fk]?.title
          if (ft) fInput.title = ft
          const req = config.fields?.[fk]?.required
          if (req !== undefined) (fInput as any).required = req
          result[fk] = fInput
        })
      }
    })

    return result
  }, [inputs, config, loanNumber])

  const hiddenFieldKeys = useMemo(() => {
    const set = new Set<string>()
    Object.entries(config.fields || {}).forEach(([key, val]) => {
      if (val?.visible === false) set.add(key)
    })
    return set
  }, [config])

  /**
   * Last visible field of the Payments group. The Other Payments editor renders right after it, so
   * the pass-through payees sit with the rest of the amount-due breakdown (P&I, Escrow Monthly,
   * Total Payment) instead of at the bottom of the tab. Null when the whole group is configured
   * away, in which case the editor falls back to the end of the Loan Details tab.
   */
  /** The chosen Servicing Fee Type, which decides which fee inputs are worth showing. */
  const servicingFeeType = String(data.servicingFeeType || '')

  const paymentsGroupLastKey = useMemo(() => {
    const keys = Object.keys(mergedInputs)
    const start = keys.indexOf('paymentsGroup')
    if (start === -1) return null
    let last: string | null = null
    for (let i = start + 1; i < keys.length; i++) {
      const inputType = (mergedInputs[keys[i]] as any)?.inputType
      if (inputType === 'section' || inputType === 'group') break
      // Total Payment and its spacer are not candidates: they render after the editor, not before.
      if (!hiddenFieldKeys.has(keys[i]) && !TRAILING_PAYMENT_KEYS.includes(keys[i])) last = keys[i]
    }
    return last
  }, [mergedInputs, hiddenFieldKeys])

  const tabInputs = useMemo(() => {
    const inputKeys = Object.keys(mergedInputs)

    // Custom sections carry their own tab in the admin config; index by section
    // key so we can resolve their tab (and their fields inherit it).
    const customTabByKey: Record<string, string> = {}
    ;(config.customSections || []).forEach((cs) => {
      customTabByKey[`custom:${cs.id}`] = cs.tab || 'Loan Details'
    })

    // Walk the flat, ordered key list. Each section header sets the active tab;
    // the fields that follow inherit it until the next section header. Built-in
    // sections resolve via getBuiltInSectionTab; custom ones via their config.
    let currentTab: string = 'Loan Details'
    return inputKeys.filter((key) => {
      const input = mergedInputs[key] as any
      if (input?.inputType === 'section') {
        currentTab = key.startsWith('custom:') ? customTabByKey[key] || 'Loan Details' : getBuiltInSectionTab(key)
      }
      return currentTab === tab
    })
  }, [mergedInputs, tab, config])

  const onChange = async (key: string, value: string, triggerBlur = false) => {
    if (isShared || loading) return
    const temp = cloneDeep(data)
    temp[key] = value
    // Total Payment is the live sum of its breakdown components.
    if (TOTAL_PAYMENT_COMPONENTS.includes(key) || key === 'otherPayments') temp.totalPayment = calcTotalPayment(temp)
    setData(temp)
    if (triggerBlur) onBlur(key, value)
  }

  const onBlur = async (key: string, _value?: any) => {
    if (isShared) return
    const value = data[key]
    // Nothing to save when the field still holds what was loaded. Imported rates and amounts load as
    // numbers, so they compare as text too, or a click in and out of one saved and logged a change.
    if (_value == undefined) {
      const original = orgData[key]
      if (
        ((value === undefined || value === null || value === '') &&
          (original === undefined || original === null || original === '')) ||
        (typeof value === 'object' && JSON.stringify(value) === JSON.stringify(original)) ||
        (typeof value !== 'object' && String(value) === String(original))
      )
        return
    }
    const savedValue = _value == undefined ? value : _value
    // Saves (and the P&I re-fetch below) run silently — no full-screen loading
    // overlay — so editing a field doesn't flash the whole screen.
    await postServicingNotes({
      key,
      value: savedValue,
    })
    setOrgData(data)
    // Saved onto the loan, not onto the servicing record (the endpoint writes the column), so the
    // store has to hear about it or Loan Structure and the ledger tiles keep quoting the old
    // reserve treatment. Read off the saved value, since a check saves itself from onChange and
    // `data` state has not landed yet at that point.
    if (key === 'chargeInterestOnReserve') dispatch(setLoanData({ key, data: savedValue }))
    // The Non-Dutch P&I and its "How?" breakdown are computed server-side; re-fetch
    // so they reflect changes to the fields that drive the calculation.
    if (PI_AFFECTING_KEYS.includes(key)) {
      const res: any = await getServicingNotes().catch(() => null)
      if (res?.success) {
        setData((prev: any) => ({
          ...prev,
          principalInterest: res.data.principalInterest,
          principalInterestBreakdown: res.data.principalInterestBreakdown,
          totalPayment: res.data.totalPayment,
          servicingFeeDue: res.data.servicingFeeDue,
          currentUpb: res.data.currentUpb,
          initialMonthlyPayment: res.data.initialMonthlyPayment,
        }))
      }
    }
    if (data.interestType !== 'Non Dutch' && PAYMENT_RECALC_KEYS.includes(key)) setShowPaymentOptions(true)
  }

  const showHistory = async (key: string, fieldLabel?: string) => {
    if (isShared) return
    const options = {
      table: 'Servicing Information',
      field: key,
      fieldLabel,
      keys: {
        field: key,
      },
      renderValue: (data: any) => <span dangerouslySetInnerHTML={{ __html: data }} />,
    }
    openAuditLog(options)
  }

  const fillDefaultRate = () => {
    const temp = cloneDeep(data)
    temp['defaultInterestRate'] = generateDocuments.defaultRate
    setData(temp)
    onBlur('defaultInterestRate', generateDocuments.defaultRate)
  }

  const onLoanFieldChange = (key: string, value: any) => {
    const json: Record<string, any> = { [key]: value }
    updateLoanFields(json).then(() => {
      dispatch(setLoanGroupData(json))
    })
  }

  // Re-read the recomputed unpaid balances and their adjustment list.
  const refreshBalances = async () => {
    const res: any = await getServicingNotes().catch(() => null)
    if (res?.success) {
      setData((prev: any) => ({
        ...prev,
        unpaidInterestBalance: res.data.unpaidInterestBalance,
        unpaidDefaultInterestBalance: res.data.unpaidDefaultInterestBalance,
        unpaidChargesBalance: res.data.unpaidChargesBalance,
        unpaidLateChargesBalance: res.data.unpaidLateChargesBalance,
        balanceAdjustments: res.data.balanceAdjustments || [],
      }))
    }
  }

  // Adjust a servicing balance by writing a signed adjustment charge (+/-).
  const onAdjustBalance = async (amount: number, note: string) => {
    if (!adjustBalance) return
    await addNewLoanCharge({
      type: ADJUSTABLE_BALANCES[adjustBalance.key],
      date: formatDateYMD('now'),
      interestFromDate: '',
      description: note || 'Balance Adjustment',
      interestRate: 0,
      originalBalance: amount,
      unpaidBalance: amount,
      accruedInterest: 0,
      totalDue: amount,
      deferred: true,
      isAdjustment: true,
    })
    await refreshBalances()
  }

  // Reverse (remove) a specific adjustment.
  const onReverseAdjustment = async (id: number) => {
    await deleteLoanCharge(id)
    await refreshBalances()
  }

  const closeOVModal = async (save: false, data: any) => {
    if (save) {
      if (ovData.parentKey) {
        onSubChange(ovData.parentKey, ovData.overrideKey, ovData.index, data.ovValue, true)
      } else {
        onChange(ovData.overrideKey, data.ovValue)
        await onBlur(ovData.overrideKey, data.ovValue)
      }
    }
    setOVData({})
  }

  const onAddAdditionalInfo = (key: string) => {
    if (isShared) return
    const temp = cloneDeep(data)
    const addonKey =
      key === 'insuranceSection'
        ? 'additionalInsurance'
        : key === 'taxSection'
        ? 'additionalTax'
        : 'additionalExtension'
    if (!temp[addonKey]) temp[addonKey] = []
    temp[addonKey] = [{ id: Date.now() }, ...temp[addonKey]]
    setData(temp)
    onBlur(addonKey, temp[addonKey])
  }

  const openInsuranceAiExtract = (index: number | null = null) => {
    setInsuranceAiTargetIndex(index)
    setShowInsuranceAiExtract(true)
  }

  const applyInsuranceAiExtract = (extracted: InsuranceAiExtractData) => {
    const temp = cloneDeep(data)
    if (!temp.additionalInsurance) temp.additionalInsurance = []

    let index = insuranceAiTargetIndex
    if (index == null || !temp.additionalInsurance[index]) {
      temp.additionalInsurance = [{ id: Date.now(), isActive: true }, ...temp.additionalInsurance]
      index = 0
    }

    const policy = temp.additionalInsurance[index]
    const moneyKeys = [
      'insurancePolicyCoverageAmount',
      'insurancePremium',
      'insuranceMonthlyPayment',
      'insuranceAmountCollected',
      'insuranceMonthCollected',
    ]
    const dateKeys = ['insuranceStartDate', 'insuranceP1Exp', 'insCancelDate', 'insInfoReq1Date']
    const textKeys = [
      'insurancePolicyNo',
      'insurancePolicyType',
      'insCancelReason',
      'insuranceAgentName',
      'insuranceAgentAddress',
      'insuranceCompanyName',
      'insurancePhoneNumber',
      'insuranceEmail',
      'insuranceNotes',
    ]

    textKeys.forEach((key) => {
      const value = (extracted as any)[key]
      if (value != null && value !== '') policy[key] = value
    })
    moneyKeys.forEach((key) => {
      const value = (extracted as any)[key]
      if (value != null && value !== '') policy[key] = thousandSeperator(value)
    })
    dateKeys.forEach((key) => {
      const value = (extracted as any)[key]
      if (value) policy[key] = formatDateYMD(value)
    })

    if (extracted.sourceFile?.fileKey) {
      const files = Array.isArray(policy.insuranceFiles) ? [...policy.insuranceFiles] : []
      if (!files.some((f: any) => f.fileKey === extracted.sourceFile!.fileKey)) {
        files.push({
          fileKey: extracted.sourceFile.fileKey,
          name: extracted.sourceFile.name || 'insurance.pdf',
          status: 'Approved',
          category: 'Trailing Docs',
        })
      }
      policy.insuranceFiles = files
    }

    temp.additionalInsurance[index] = policy
    setData(temp)
    onBlur('additionalInsurance', temp.additionalInsurance)
    toast('Insurance fields updated from document.', { type: 'success' })
  }

  const onRemoveAdditionalInfo = async (key: string, index: number) => {
    if (isShared) return
    const isContinue = await confirm(
      `Are you sure you want to remove this additional ${
        key === 'additionalInsurance' ? 'Insurance' : 'Tax'
      } Information?`,
    )
    if (!isContinue) return
    const temp = cloneDeep(data)
    if (!temp[key]) return
    temp[key].splice(index, 1)
    setData(temp)
    onBlur(key, temp[key])
  }

  const onSubChange = (key: string, subKey: string, index: number, value: any, triggerBlur = false) => {
    if (isShared || loading) return
    const temp = cloneDeep(data)
    if (!temp[key] || !temp[key][index]) return
    temp[key][index][subKey] = value
    setData(temp)
    if (triggerBlur) onBlur(key, temp[key])
  }

  const handleReminderSave = (reminder: Record<string, any>) => {
    const { id: reminderId } = reminder
    // Store the reminder ID for the current tab
    const reminderKey = REMINDER_TAB_KEYS[tab]
    if (!reminderKey) return

    // Update reminder IDs state
    setReminderIds((prev) => ({
      ...prev,
      [tab]: reminderId,
    }))

    // Save to backend
    onChange(reminderKey, String(reminderId))
    onBlur(reminderKey, String(reminderId))
    setIsReminderOpen(false)
  }

  const onDeleteReminder = async () => {
    const isConfirmed: any = await confirm('Are you sure you want to remove this reminder?')
    if (isConfirmed === false) return

    const currentReminderId = reminderIds[tab]
    const res = await deleteReminder(currentReminderId)
    if (res.success) {
      toast('Successfully deleted a Reminder', { type: 'success' })

      // Update reminder IDs state
      setReminderIds((prev) => ({
        ...prev,
        [tab]: -1,
      }))

      // Clear from backend
      const reminderKey = REMINDER_TAB_KEYS[tab]
      if (!reminderKey) return
      onChange(reminderKey, String(-1))
      onBlur(reminderKey, String(-1))
    }
  }

  const onCloseRolodexModal = (_data: Record<string, any>) => {
    setShowRolodexModal(false)
    if (selectedInsuranceItem == null) return
    const temp = cloneDeep(data)
    temp.additionalInsurance[selectedInsuranceItem].insuranceAgentNameOV = getFullName(
      _data.FirstName,
      _data.MiddleName,
      _data.LastName,
    )
    temp.additionalInsurance[selectedInsuranceItem].insuranceCompanyNameOV = _data.Company || ' '
    temp.additionalInsurance[
      selectedInsuranceItem
    ].insuranceAgentAddressOV = `${_data.Street}, ${_data.City}, ${_data.State} ${_data.Zip}`
    temp.additionalInsurance[selectedInsuranceItem].insurancePhoneNumberOV = _data.WorkPhone || ' '
    temp.additionalInsurance[selectedInsuranceItem].insuranceEmailOV = _data.EMail || ' '
    setData(temp)
    onBlur('additionalInsurance', temp.additionalInsurance)
  }

  const otherPaymentsEditor = (
    <OtherPaymentsEditor
      value={data.otherPayments || []}
      onChange={(rows) =>
        setData((prev: any) => {
          const next = { ...prev, otherPayments: rows }
          return { ...next, totalPayment: calcTotalPayment(next) }
        })
      }
      onCommit={(rows) => onBlur('otherPayments', rows)}
      payees={payees}
      disabled={isShared || readOnlyFieldKeys.has('otherPayments')}
      hint="Default extra payees collected with each payment. These prefill the payment screen and are part of the Total Payment due."
    />
  )

  return (
    <div className="servicing-note-container">
      <LayoutLoading show={loading} />
      {data.hold && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-amber-800">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
          <div className="text-sm">
            <span className="font-semibold">This loan is on Hold.</span> Applying payments, sending statements, and
            borrower communications are blocked while Hold is on. Turn off <span className="font-medium">Hold</span>{' '}
            below to resume these actions.
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-2 gap-3">
        <h2 className="text-2xl flex items-center gap-1 font-bold">
          <span>Servicing Information</span>
        </h2>
        {!isShared && (
          <Button link onClick={() => setShowAmortization(true)}>
            Amortization Schedule
          </Button>
        )}
      </div>
      <ButtonGroup title={tabs} value={tab} onChange={setTab} />
      <div className="w-full">
        <div className="grid grid-cols-12 gap-2">
          {Object.keys(mergedInputs).map((key, index) => {
            if (!tabInputs.includes(key)) return
            if (hiddenFieldKeys.has(key)) return
            const input = mergedInputs[key]
            // Accrual Period only applies to Dutch interest, so hide it when
            // Non-Dutch. Daily Rate Basis still applies (its 360/365 denominator
            // drives the Non-Dutch daily-balance interest), so it stays visible.
            const isNonDutch = data.interestType === 'Non Dutch'
            if (key === 'accrualPeriod' && isNonDutch) return
            // The reserve toggle is the mirror image: only a Non-Dutch loan has a choice to make
            // about its interest reserve, and only one that carries a reserve at all. The screen's
            // own Interest Type wins over the backend's resolved answer so the field appears and
            // disappears the moment the servicer changes it, without a reload.
            if (key === 'chargeInterestOnReserve') {
              const reserveNonDutch = (data.interestType || data.reserveInterestType) === 'Non Dutch'
              if (!data.hasInterestReserve || !reserveNonDutch) return
            }
            // `ptSpacer` only existed to push Total Payment into the right column; col-start-9
            // does that now, and an empty label in the flow just opens a gap.
            if (key === 'ptSpacer') return
            // Adjustable-rate terms only mean anything on an adjustable-rate loan.
            if (ARM_FIELD_KEYS.includes(key) && data.rateType !== 'Adjustable Rate') return
            // Only the servicing-fee inputs the chosen Fee Type actually reads. With all of them
            // on screen at once, switching the type looked like it changed nothing: the fields
            // that stopped mattering stayed put, still holding their old values.
            if (SERVICING_FEE_FIELD_TYPES[key] && !SERVICING_FEE_FIELD_TYPES[key].includes(servicingFeeType)) return
            const overrideKey = `${key}OV`
            const isFloodFreeEntry = floodCertKeys.includes(key) && !data.floodCertValues?.[key]
            input.value = !isFloodFreeEntry && data[overrideKey] ? data[overrideKey] : data[key]
            if (input.type === 'date' && input.value?.length > 0) {
              input.value = formatDateYMD(input.value)
            }
            if (key === 'insuranceFiles') input.value = data.insuranceFiles ?? []
            // FileTable expects an array; default empty so the table renders properly
            // even when nothing has been uploaded for a (custom) FileTable field yet.
            if (input.inputType === 'filetable') input.value = data[key] ?? []
            input.history = true

            if (data[overrideKey] && !isFloodFreeEntry) input.bgColor = 'red-50'
            else input.bgColor = ''

            input.error = InputValidate(input)

            if (isShared) {
              input.readOnly = true
              input.disabled = true
              input.additionalElements = null
            } else if (calcKeys.includes(key) && !isFloodFreeEntry) {
              input.disabled = true
              input.additionalElements = (
                <span
                  className="mx-2 text-shade-blue cursor-pointer"
                  onClick={() => {
                    setOVData({
                      title: input.title,
                      overrideKey,
                      calcValue: data[key] || '',
                      ovValue: data[overrideKey] || '',
                      input: { ...input, disabled: false, additionalElements: null, readOnly: false, bgColor: '' },
                    })
                  }}
                >
                  <PencilSquareIcon className="w-4 h-4" />
                </span>
              )
            }
            if (key === 'principalInterest') {
              // The "How?" link explains how the P&I was calculated and is available for every
              // interest type whenever the server returned a breakdown.
              const howLink =
                !isShared && data.principalInterestBreakdown ? (
                  <span
                    className="mx-2 text-shade-blue hover:underline cursor-pointer text-xs"
                    onClick={() => setShowPICalc(true)}
                  >
                    How?
                  </span>
                ) : null
              if (isShared || isNonDutch) {
                // Shared / Non-Dutch: P&I is a read-only calculated value.
                input.disabled = true
                input.readOnly = true
                input.additionalElements = howLink
              } else {
                // Dutch: P&I stays editable, with "How?" alongside the "Options" link.
                input.disabled = false
                input.additionalElements = (
                  <>
                    {howLink}
                    <span
                      className="mx-2 text-shade-blue hover:underline cursor-pointer text-xs"
                      onClick={() => setShowPaymentOptions(true)}
                    >
                      Options
                    </span>
                  </>
                )
              }
            }
            if (!isShared && key === 'defaultInterestRate')
              input.additionalElements = (
                <span
                  className="mx-2 text-shade-blue hover:underline cursor-pointer font-variation-settings-600"
                  onClick={fillDefaultRate}
                >
                  <Tooltip message="Fill Original Interest Rate">
                    <span>Fill</span>
                  </Tooltip>
                </span>
              )
            if (!isShared && ADJUSTABLE_BALANCES[key])
              input.additionalElements = (
                <span
                  className="mx-2 text-shade-blue cursor-pointer"
                  onClick={() => setAdjustBalance({ key, title: input.title || '' })}
                >
                  <Tooltip message="Adjust balance">
                    <PencilSquareIcon className="w-4 h-4" />
                  </Tooltip>
                </span>
              )
            if (!isShared && ['servicerLoanNumber', 'servicerLoanNumber2', 'byteLoanNumber'].includes(key)) {
              const activeFci = key === 'servicerLoanNumber' ? 0 : key === 'servicerLoanNumber2' ? 1 : 2
              input.additionalElements = (
                <Toggle
                  id={`active-fci-${activeFci}`}
                  title=""
                  value={loan.activeFci === activeFci}
                  onChange={() => onLoanFieldChange('activeFci', activeFci)}
                />
              )
            }

            if (!isShared && ['insuranceSection', 'taxSection', 'extensionSection'].includes(key)) {
              input.additionalElements = (
                <div className="flex justify-end gap-3 items-center">
                  {key === 'insuranceSection' && canInsuranceAiExtract && (
                    <span
                      className="text-shade-blue cursor-pointer hover:underline"
                      onClick={() => openInsuranceAiExtract(null)}
                    >
                      Extract from PDF
                    </span>
                  )}
                  <span
                    className="text-shade-blue cursor-pointer hover:underline"
                    onClick={() => onAddAdditionalInfo(key)}
                  >
                    + Add
                  </span>
                </div>
              )
            }
            // Group-level edit permission. Render read-only and drop the inline
            // shortcuts (override pencil, balance adjust, payment options) that
            // would otherwise let the value be changed anyway.
            if (readOnlyFieldKeys.has(key) || isShared) {
              input.disabled = true
              input.readOnly = true
              input.additionalElements = null
              input.history = false
            }
            // After the read-only pass on purpose: anyone who can see the loan can look at the index's
            // history. Entering values inside it is gated by its own permission.
            if (key === 'armIndex' && !isShared && data.armIndexInfo) {
              input.additionalElements = (
                <span
                  className="mx-2 text-shade-blue hover:underline cursor-pointer text-xs"
                  onClick={() => setIndexValuesFor(data.armIndexInfo)}
                >
                  Values
                </span>
              )
            }

            const rtl = [
              <div
                key={index}
                className={`col-span-${input.span || 4} ${gridColumnStart(key)}`}
              >
                <RenderInput
                  input={input}
                  Key={key}
                  // FileTable inputs save themselves via the third `triggerBlur`
                  // arg (RenderInput passes `true`); the built-in `insuranceFiles`
                  // field has the same behavior.
                  onChange={(k: string, value: any, triggerBlur?: boolean) =>
                    onChange(k, value, !!triggerBlur || k === 'insuranceFiles' || input.inputType === 'filetable')
                  }
                  onBlur={(key: string) => onBlur(key)}
                  showHistory={isShared ? undefined : () => showHistory(key, input.title)}
                />
              </div>,
            ]

            // What the next adjustment will do, right under the Adjustable Rate header. Keyed on the
            // saved terms so it refreshes after each save, not on every keystroke.
            if (key === 'armGroup' && !isShared) {
              rtl.push(
                <div key="armPreview" className="col-span-full">
                  <ArmAdjustmentPreview
                    refreshKey={JSON.stringify([...ARM_FIELD_KEYS, 'armRefreshedAt'].map((armKey) => orgData[armKey] ?? ''))}
                    onAdjusted={async () => {
                      // The adjustment moved the ARM dates and index value on the server; pull them in.
                      const res: any = await getServicingNotes().catch(() => null)
                      if (!res?.success) return
                      const armPatch: Record<string, any> = {}
                      ;[...ARM_FIELD_KEYS, 'currentInterestRate'].forEach((armKey) => (armPatch[armKey] = res.data[armKey]))
                      setData((prev: any) => ({ ...prev, ...armPatch }))
                      setOrgData((prev: any) => ({ ...prev, ...armPatch }))
                      setInputs(res.inputs)
                    }}
                  />
                </div>,
              )
            }

            if (key === paymentsGroupLastKey && !isShared) {
              rtl.push(
                <div key="otherPayments" className="col-span-full mt-2">
                  {otherPaymentsEditor}
                </div>,
              )
            }

            const extensionInputKeys = Object.keys(extensionInputs)

            const lastInsuranceField = 'insuranceSection'
            const lastTaxField = 'taxSection'
            const lastExtensionField = extensionInputKeys[extensionInputKeys.length - 1]

            if ([lastInsuranceField, lastTaxField, lastExtensionField].includes(key)) {
              const addonKey =
                key === lastInsuranceField
                  ? 'additionalInsurance'
                  : key === lastTaxField
                  ? 'additionalTax'
                  : 'additionalExtension'
              if (data[addonKey]?.length) {
                data[addonKey].map((item: any, subIndex: number) => {
                  const subInputs =
                    key === lastInsuranceField
                      ? cloneDeep(insuranceInputs)
                      : key === lastTaxField
                      ? cloneDeep(taxInputs)
                      : cloneDeep(extensionInputs)
                  rtl.push(
                    <div
                      key={`${addonKey}-${item.id}-header`}
                      className="flex justify-end gap-2 pb-2 items-center col-span-full border-b-[1px] border-gray-500 border-solid"
                    >
                      <RenderInput
                        input={{
                          inputType: 'toggle',
                          title: 'Active',
                          value: item.isActive,
                          disabled: isShared,
                          readOnly: isShared,
                        }}
                        Key={`addonKey-isActive-${subIndex}`}
                        onChange={(key: string, value: any) => onSubChange(addonKey, 'isActive', subIndex, value)}
                        onBlur={() => onBlur(addonKey)}
                      />
                      <RenderInput
                        input={{
                          inputType: 'toggle',
                          title: 'Hide',
                          value: item.isHidden,
                          disabled: isShared,
                          readOnly: isShared,
                        }}
                        Key={`addonKey-isHidden-${subIndex}`}
                        onChange={(key: string, value: any) => onSubChange(addonKey, 'isHidden', subIndex, value)}
                        onBlur={() => onBlur(addonKey)}
                      />
                      {!isShared && (
                        <span
                          className="text-red-500 flex items-center gap-2 cursor-pointer hover:underline"
                          onClick={() => onRemoveAdditionalInfo(addonKey, subIndex)}
                        >
                          <TrashIcon className="w-4 h-4" />
                          Delete
                        </span>
                      )}
                      {!isShared && addonKey === 'additionalInsurance' && canInsuranceAiExtract && (
                        <span
                          className="text-shade-blue cursor-pointer hover:underline"
                          onClick={() => openInsuranceAiExtract(subIndex)}
                        >
                          Extract from PDF
                        </span>
                      )}
                    </div>,
                  )
                  if (!item.isHidden) {
                    Object.keys(subInputs).map((subKey: string) => {
                      const subInput = (subInputs as any)[subKey]
                      const subOverrideKey = `${subKey}OV`
                      subInput.value = item[subKey]
                      if (calcKeys.includes(subKey))
                        subInput.value =
                          item[subOverrideKey] || data[`${subKey}${subIndex}`] || data[subKey] || item[subKey] || ''
                      if (subKey === 'insuranceFiles') subInput.value = item.insuranceFiles || []
                      // Preserve legacy custom policy types that aren't in the new dropdown list.
                      if (
                        subKey === 'insurancePolicyType' &&
                        subInput.value &&
                        Array.isArray(subInput.options) &&
                        !subInput.options.includes(subInput.value)
                      ) {
                        subInput.options = [...subInput.options, subInput.value]
                      }
                      if (subInput.type === 'date' && subInput.value?.length > 0) {
                        subInput.value = formatDateYMD(subInput.value)
                      }
                      subInput.history = !isShared
                      subInput.error = InputValidate(subInput)

                      if (item[subOverrideKey]) subInput.bgColor = 'red-50'
                      else subInput.bgColor = ''

                      if (isShared) {
                        subInput.disabled = true
                        subInput.readOnly = true
                        subInput.additionalElements = null
                      } else if (calcKeys.includes(subKey)) {
                        subInput.disabled = true
                        subInput.additionalElements = (
                          <span
                            className="mx-2 text-shade-blue cursor-pointer"
                            onClick={() => {
                              setOVData({
                                title: subInput.title,
                                parentKey: addonKey,
                                index: subIndex,
                                overrideKey: subOverrideKey,
                                calcValue: data[subKey] || '',
                                ovValue: item[subOverrideKey] || '',
                              })
                            }}
                          >
                            <PencilSquareIcon className="w-4 h-4" />
                          </span>
                        )
                        if (subKey === 'insuranceCompanyName') {
                          subInput.additionalElements = (
                            <>
                              <span
                                className="mx-2 text-shade-blue cursor-pointer"
                                onClick={() => {
                                  setOVData({
                                    title: subInput.title,
                                    parentKey: addonKey,
                                    index: subIndex,
                                    overrideKey: subOverrideKey,
                                    calcValue: data[subKey] || '',
                                    ovValue: item[subOverrideKey] || '',
                                  })
                                }}
                              >
                                <PencilSquareIcon className="w-4 h-4" />
                              </span>
                              <span
                                className="mx-2 text-shade-blue cursor-pointer hover:underline"
                                onClick={() => {
                                  setShowRolodexModal(true)
                                  setSelectedInsuranceItem(subIndex)
                                }}
                              >
                                Import Parties
                              </span>
                            </>
                          )
                        }
                      }

                      rtl.push(
                        <div
                          key={`${addonKey}-${item.id}-${subKey}`}
                          className={`col-span-${subInput.span || 4} ${!item.isActive ? 'opacity-50' : ''}`}
                        >
                          <RenderInput
                            input={subInput}
                            Key={`${addonKey}-${item.id}-${subKey}`}
                            onChange={(key: string, value: any) =>
                              onSubChange(addonKey, subKey, subIndex, value, subKey === 'insuranceFiles')
                            }
                            onBlur={() => onBlur(addonKey)}
                            showHistory={
                              isShared
                                ? undefined
                                : () =>
                                    showHistory(
                                      `${addonKey}-${item.id}-${subKey}`,
                                      `${subInput.title} - ${subIndex + 1}`,
                                    )
                            }
                          />
                        </div>,
                      )
                    })
                  }
                })
              }
            }
            return rtl
          })}
        </div>
        {tab === 'Loan Details' && !isShared && !paymentsGroupLastKey && <div className="mt-4">{otherPaymentsEditor}</div>}
        {!isShared && REMINDER_TAB_KEYS[tab] && (
          <div className="mt-4 flex justify-end items-center gap-2">
            <span className="text-shade-blue cursor-pointer hover:underline" onClick={() => setIsReminderOpen(true)}>
              {reminderIds[tab] !== -1 ? '✏ Edit Reminder' : '+ Add Reminder'}
            </span>
            {reminderIds[tab] !== -1 && (
              <span className="text-red-500 cursor-pointer hover:text-red-700" onClick={onDeleteReminder}>
                <Tooltip message="Delete Reminder">
                  <TrashIcon className="w-4 h-4" />
                </Tooltip>
              </span>
            )}
          </div>
        )}
      </div>
      {!isShared && Object.keys(ovData).length > 0 && (
        <OverrideCaclModal
          title={ovData.title}
          calcValue={ovData.calcValue}
          ovValue={ovData.ovValue}
          onClose={closeOVModal}
          input={ovData.input}
        />
      )}
      {!isShared && isReminderOpen && (
        <ReminderDetailModal
          reminderId={reminderIds[tab]}
          userEmails={userEmails}
          onModalClose={() => setIsReminderOpen(false)}
          onModalOk={handleReminderSave}
          loanNumber={loanNumber}
        />
      )}
      {!isShared && isShowRolodexModal && <RolodexModal type={'Hazard Insurance Company'} onClose={onCloseRolodexModal} />}
      {!isShared && showInsuranceAiExtract && (
        <InsuranceAiExtractModal
          isOpen={showInsuranceAiExtract}
          loanNumber={Number(loanNumber)}
          onClose={() => setShowInsuranceAiExtract(false)}
          onApply={applyInsuranceAiExtract}
        />
      )}
      {!isShared && showAmortization && (
        <AmortizationScheduleModal
          data={data}
          loanNumber={Number(loanNumber)}
          byteproFileName={data.byteproFileName}
          onClose={() => setShowAmortization(false)}
        />
      )}
      {!isShared && adjustBalance && (
        <AdjustBalanceModal
          title={adjustBalance.title}
          currentBalance={Number(String(data[adjustBalance.key] ?? '').replace(/,/g, '')) || 0}
          adjustments={(data.balanceAdjustments || []).filter(
            (a: any) => a.type === ADJUSTABLE_BALANCES[adjustBalance.key],
          )}
          onClose={() => setAdjustBalance(null)}
          onAdd={onAdjustBalance}
          onReverse={onReverseAdjustment}
        />
      )}
      {!isShared && showPaymentOptions && (
        <PaymentOptionsModal
          data={data}
          onClose={() => setShowPaymentOptions(false)}
          onSelect={(value: number) => {
            onChange('principalInterest', String(value), true)
            setShowPaymentOptions(false)
          }}
        />
      )}
      {indexValuesFor && (
        <RateIndexValuesModal
          index={indexValuesFor}
          onClose={async (changed) => {
            setIndexValuesFor(null)
            // A new value changes the picker's "since" label and the adjustment preview.
            if (changed) {
              const res: any = await getServicingNotes().catch(() => null)
              if (res?.success) {
                setInputs(res.inputs)
                setOrgData({ ...orgData, armRefreshedAt: Date.now() })
              }
            }
          }}
        />
      )}
      {!isShared && showPICalc && (
        <PaymentInterestCalcModal
          breakdown={data.principalInterestBreakdown}
          rate={data.currentInterestRate}
          onClose={() => setShowPICalc(false)}
        />
      )}
    </div>
  )
}
